import { useState, useEffect, useRef } from 'react'
import api from '../../services/api.js'
import { useTranslation } from 'react-i18next'

// Matches MAX_FILES in backend/app/routers/feedback.py — the cap is enforced there too.
const MAX_FILES = 5


// Inject the attention animations once.
let _fbStyles = false
function injectFbStyles() {
  if (_fbStyles || typeof document === 'undefined') return
  _fbStyles = true
  const s = document.createElement('style')
  s.textContent = `
    @keyframes fb-ring { 0%{box-shadow:0 0 0 0 rgba(79,70,229,.4)} 70%{box-shadow:0 0 0 7px rgba(79,70,229,0)} 100%{box-shadow:0 0 0 0 rgba(79,70,229,0)} }
    @keyframes fb-nudge { 0%,90%,100%{transform:rotate(0)} 93%{transform:rotate(-12deg)} 96%{transform:rotate(9deg)} 98%{transform:rotate(-4deg)} }
    /* bubble emanates from the icon (slides out from the right) then vibrates like an announcement */
    @keyframes fb-bubble-in { from{opacity:0; transform:translateX(10px) scale(.9)} to{opacity:1; transform:translateX(0) scale(1)} }
    @keyframes fb-shake { 0%,100%{transform:translateX(0)} 18%{transform:translateX(-2px)} 36%{transform:translateX(2px)} 54%{transform:translateX(-1.5px)} 72%{transform:translateX(1.5px)} 88%{transform:translateX(-1px)} }
    .fb-ring{ animation: fb-ring 2.6s ease-out infinite; }
    .fb-nudge{ animation: fb-nudge 7s ease-in-out infinite; transform-origin:center; }
    .fb-bubble{ animation: fb-bubble-in .3s ease-out, fb-shake .6s ease-in-out .3s; }
  `
  document.head.appendChild(s)
}

const FB_MSG = 'Share your feedback'

/**
 * Feedback / Suggestions / Issues widget — lives in the top bar beside the
 * theme toggle on every dashboard. Submissions are filed as GitHub Issues
 * by the backend (POST /feedback/). A subtle pulse + a periodic "announcement"
 * bubble invites feedback, and calms down once the user has opened it.
 */
export default function FeedbackButton() {
  const { t: tc } = useTranslation('common')
  const { t: tch } = useTranslation('chrome')
  injectFbStyles()
  const [open, setOpen] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const pauseUntil = useRef(0)   // briefly stop reminders right after the user opens feedback

  // Gentle recurring reminder: the "Share your feedback" bubble announces out of
  // the icon every ~22s for ~4s. It pauses while the modal is open and for a
  // short cooldown after the user has just used it — never permanently silenced.
  useEffect(() => {
    let hideT
    const tick = () => {
      if (open || Date.now() < pauseUntil.current) return
      setShowBubble(true)
      hideT = setTimeout(() => setShowBubble(false), 4000)
    }
    const first = setTimeout(tick, 3500)
    const loop = setInterval(tick, 22000)
    return () => { clearTimeout(first); clearTimeout(hideT); clearInterval(loop) }
  }, [open])

  const openModal = () => {
    setOpen(true); setShowBubble(false)
    pauseUntil.current = Date.now() + 90000   // 90s quiet period after using it
  }
  const [type, setType] = useState('Suggestion')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)   // { ok, text, url }

  const fileInput = useRef(null)

  // ADDS to what is already chosen instead of replacing it. The browser's picker returns one
  // selection and nothing more, so replacing meant a second screenshot silently discarded the
  // first — which is why attaching more than one file only worked if they happened to sit in the
  // same folder and were picked in a single go.
  const addFiles = (e) => {
    const picked = Array.from(e.target.files || [])
    setFiles(prev => {
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`))
      const next = [...prev]
      for (const f of picked) {
        const key = `${f.name}:${f.size}`
        if (!seen.has(key) && next.length < MAX_FILES) { next.push(f); seen.add(key) }
      }
      return next
    })
    // Clear the input, or choosing the SAME file again fires no change event and looks broken.
    if (fileInput.current) fileInput.current.value = ''
  }
  const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i))

  const reset = () => {
    setType('Suggestion'); setTitle(''); setDescription(''); setFiles([]); setResult(null)
    if (fileInput.current) fileInput.current.value = ''
  }
  const close = () => { setOpen(false); reset() }

  const submit = async (e) => {
    e.preventDefault()
    if (submitting) return
    if (!title.trim()) { setResult({ ok: false, text: 'Please add a short title.' }); return }
    if (files.length > MAX_FILES) { setResult({ ok: false, text: `Please attach at most ${MAX_FILES} files.` }); return }
    setSubmitting(true)
    setResult(null)
    try {
      const fd = new FormData()
      fd.append('type', type)
      fd.append('title', title)
      fd.append('description', description)
      fd.append('page', window.location.pathname)
      files.forEach(f => fd.append('files', f))
      const r = await api.post('/feedback/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setResult({ ok: true, text: r.data?.detail || 'Thank you! Your feedback was filed.', url: r.data?.url })
      setTitle(''); setDescription(''); setFiles([])
    } catch (err) {
      setResult({ ok: false, text: err.response?.data?.detail || 'Could not submit feedback. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <div className="relative">
        <button
          onClick={openModal}
          className="fb-ring p-2 rounded-full text-indigo-500 hover:text-indigo-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label={tch('aria_send_feedback')}
          title={tch('t_feedback_widget')}
        >
          <svg className="fb-nudge w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
        </button>

        {/* Recurring "announcement" bubble — emanates from the icon, vibrates, fades out */}
        {showBubble && !open && (
          <div className="fb-bubble absolute right-0 top-full mt-2 z-50 whitespace-nowrap">
            <button
              onClick={openModal}
              className="flex items-center gap-1.5 bg-indigo-600 text-white text-xs font-medium px-3 py-1.5 rounded-xl shadow-lg shadow-indigo-500/30 hover:bg-indigo-700"
            >
              {FB_MSG}
            </button>
            <div className="absolute -top-1 right-4 w-2.5 h-2.5 bg-indigo-600 rotate-45" />
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-60 p-4" onClick={close}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{tch('share_feedback')}</h2>
                <p className="text-xs text-gray-500">Suggestions, ideas, or problems — they reach our team directly.</p>
              </div>
              <button onClick={close} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>

            {result?.ok ? (
              <div className="text-center py-6">
                <div className="text-4xl mb-3">🙏</div>
                <p className="text-base font-semibold text-gray-900">{tch('thank_you')}</p>
                <p className="text-sm text-gray-600 mt-1.5">
                  We've received it and our team will review it and work on it. We really
                  appreciate you helping us improve ProTrack.
                </p>
                <div className="mt-5">
                  <button onClick={close} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">{tch('done')}</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.category')}</label>
                  <select value={type} onChange={e => setType(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Suggestion">{tch('fb_suggestion')}</option>
                    <option value="Bug">{tch('fb_bug')}</option>
                    <option value="Issue">{tch('fb_issue')}</option>
                    <option value="Feedback">{tch('fb_feedback')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tc('labels.name')}</label>
                  <input value={title} onChange={e => setTitle(e.target.value)} placeholder={tch('ph_short_summary')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tch('details')}</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4}
                    placeholder="What happened, or what would you like to see? Steps to reproduce help for bugs."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{tch('attachments_optional')}</label>
                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    disabled={files.length >= MAX_FILES}
                    accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp4"
                    onChange={addFiles}
                    className="w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:text-xs hover:file:bg-indigo-100 disabled:opacity-50"
                  />
                  {files.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {files.map((f, i) => (
                        <li key={`${f.name}-${f.size}-${i}`}
                          className="flex items-center gap-2 text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                          <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                          <span className="shrink-0 text-gray-400">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                          <button type="button" onClick={() => removeFile(i)}
                            className="shrink-0 w-4 h-4 leading-none text-gray-400 hover:text-red-600"
                            aria-label={`Remove ${f.name}`}>×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[11px] text-gray-500 mt-1">
                    {files.length >= MAX_FILES
                      ? `${MAX_FILES} of ${MAX_FILES} attached — remove one to add another.`
                      : `${files.length} of ${MAX_FILES} attached · add them one at a time or several at once · screenshots, PDF, Office docs, or short MP4 (max 25 MB each)`}
                  </p>
                </div>
                {result && !result.ok && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{result.text}</div>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={close} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">{tc('actions.cancel')}</button>
                  <button type="submit" disabled={submitting} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                    {submitting ? 'Sending…' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  )
}
